import { useEffect } from 'react';
import type { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import HighscoreDialog from '../../components/organisms/HighscoreDialog';
import SpeechBubble from '../../components/atoms/SpeechBubble';
import RoundButton from '../../components/atoms/RoundButton.Container';
import AutofitText from '../../components/molecules/AutofitText';
import Text from '../../components/atoms/Text';
import Image from '../../components/atoms/Image';
import SoundCSSTransition from '../../components/atoms/SoundCSSTransition.Container';
import type { Points, TotalScore } from '../../data/Score';
import type { HighscoreEntry } from '../../data/Highscore';
import Assets from '../../assets/Assets';
import ButtonAnimation from '../../assets/styles/Button.module.css';

export interface StateProps {
	playerName: string;
	score: TotalScore;
	points: Points;
	highscoreEntries: HighscoreEntry[];
}

export interface DispatchProps {
	loadHighscore: () => Promise<void>;
	addHighscoreEntry: (playerName: string, score: TotalScore) => Promise<void>;
}

export interface OwnProps {
	onFinish: () => void;
	onAgain: () => void;
}

type Props = StateProps & DispatchProps & OwnProps;

const SummaryScreen: FunctionComponent<Props> = (props: Props) => {
	const {
		playerName,
		score,
		points,
		highscoreEntries,
		onFinish,
		onAgain,
		loadHighscore,
		addHighscoreEntry
	} = props;
	const { t } = useTranslation();

	useEffect(() => {
		const loadAndAdd = async () => {
			await addHighscoreEntry(playerName, score);
			await loadHighscore();
		};
		loadAndAdd();
	}, [playerName, score, loadHighscore, addHighscoreEntry]);

	return (
		<>
			<HighscoreDialog
				playerName={playerName}
				score={score}
				points={points}
				highscoreEntries={highscoreEntries}
				onClose={onFinish}
			/>
			{/* Narrator */}
			<>
				<SoundCSSTransition
					classNames="narrator"
					soundUrl={Assets.sounds.personMoves}
					timeout={1_000}
					mountOnEnter
				>
					<Image
						src={Assets.images['narrator.amazed.body']}
						width="39vmin"
						height="56vmin"
						position="absolute"
						left="0"
						bottom="0"
					/>
				</SoundCSSTransition>
				<SpeechBubble
					anchor="left_bottom"
					width="48vmin"
					height="34vmin"
					position="absolute"
					left="38vmin"
					bottom="26vmin"
					fadeInDelay={1_000}
					fadeInDuration={300}
				>
					<AutofitText>
						<Text
							content={t('summary.congratulation', {
								returnObjects: true,
								replace: { playerName }
							})}
						/>
					</AutofitText>
				</SpeechBubble>
				<RoundButton
					className={ButtonAnimation.appearDelayed}
					onClick={onAgain}
					width="32vmin"
					height="20vmin"
					position="absolute"
					left="calc(38vmin + 48vmin - 34vmin)"
					bottom="calc(26vmin - 15vmin)"
				>
					<Trans>summary.again</Trans>
				</RoundButton>
			</>
		</>
	);
};

export default SummaryScreen;
